import { ColumnType } from "../../../../../../shared/infrastructure/ui/components/libs/table/types";
import { FormatProps } from "../../../../../../shared/domain/entities/table-formats";
import { SortByHeader } from "../../../../../../shared/infrastructure/ui/components/libs/table/sort-by-header";
import { capitalize } from "lodash";
import {
  CurrencyFormat,
  DateFormat,
} from "../../../../../../shared/infrastructure/ui/components/libs/table/formats";
import { HistoryLogT, LocationT } from "../../../../domain";
import {
  OrderTypes,
  SortByType,
  SortByValuesType,
} from "../../../../../../shared/domain/entities/fetch-entities";
import React, { useMemo, useState } from "react";

type OfferFormatProps = Pick<FormatProps, "format" | "style"> & {
  data?: LocationT;
};

const LocationFormat = ({ data }: OfferFormatProps): React.ReactElement => {
  if (data === undefined) return <span>- -</span>;

  const { city, state_code, state_id } = data;

  return (
    <span>
      {(city && state_code) || state_id
        ? `${city || ""}, ${state_code ?? state_id}`
        : "- -"}
    </span>
  );
};

const MilesFormat = ({
  data,
}: {
  data?: number | null;
}): React.ReactElement => {
  if (data === undefined) return <span>- -</span>;

  return <span>{data === 0 || data ? `${Math.round(data)} mi` : "- -"}</span>;
};

export const useLogsTableColumns = (
  showStatus = false
): {
  logsTableColumns: Array<ColumnType>;
  sortByValues: SortByValuesType;
} => {
  const [sortByValues, setSortByValues] = useState<SortByValuesType>({
    sort_by: SortByType.SHIP_DATE,
    order_by: OrderTypes.DESC,
  });

  const handleSortByValue = (value: SortByType): void => {
    setSortByValues((prevState) => ({
      ...prevState,
      sort_by: value,
      order_by:
        prevState.sort_by !== value
          ? OrderTypes.ASC
          : prevState.order_by === OrderTypes.ASC
          ? OrderTypes.DESC
          : OrderTypes.ASC,
    }));
  };

  const logsTableColumns = useMemo((): Array<ColumnType> => {
    const columns: Array<ColumnType> = [
      {
        prop: "carrier_id",
        header: (
          <SortByHeader
            headerText="Carrier ID"
            onChange={() => handleSortByValue(SortByType.CARRIER_ID)}
            value={sortByValues}
            headerKey={SortByType.CARRIER_ID}
          />
        ),
        align: "center",
      },
      {
        prop: "order_number",
        header: (
          <SortByHeader
            headerText="Order No."
            onChange={() => handleSortByValue(SortByType.ORDER_NUMBER)}
            value={sortByValues}
            headerKey={SortByType.ORDER_NUMBER}
          />
        ),
        align: "center",
      },
      {
        prop: "origin",
        header: "Origin",
        component: {
          component: LocationFormat,
        },
        align: "center",
      },
      {
        prop: "destination",
        header: "Destination",
        component: {
          component: LocationFormat,
        },
        align: "center",
      },
      {
        prop: "distance",
        header: "Miles",
        component: {
          component: MilesFormat,
        },
        align: "center",
      },
      {
        prop: "rate",
        header: "Rate",
        component: {
          component: CurrencyFormat,
        },
        align: "center",
      },
      {
        prop: "origin.arrive_early_datetime",
        header: (
          <SortByHeader
            headerText="Ship Date"
            onChange={() => handleSortByValue(SortByType.SHIP_DATE)}
            value={sortByValues}
            headerKey={SortByType.SHIP_DATE}
          />
        ),
        component: {
          component: DateFormat,
          props: {
            format: "MM/DD/YYYY",
          },
        },
        align: "center",
      },
      {
        prop: "destination.arrive_early_datetime",
        header: (
          <SortByHeader
            headerText="Delivery Date"
            onChange={() => handleSortByValue(SortByType.DELIVERY_DATE)}
            value={sortByValues}
            headerKey={SortByType.DELIVERY_DATE}
          />
        ),
        component: {
          component: DateFormat,
          props: {
            format: "MM/DD/YYYY",
            style: {
              padding: 5,
              fontSize: 12,
            },
          },
        },
        align: "center",
      },
    ];

    if (showStatus) {
      return [
        ...columns,
        {
          prop: "all",
          header: "Status",
          component: {
            component: ({
              data,
            }: {
              data?: HistoryLogT;
            }): React.ReactElement => (
              <div
                style={{
                  color: `${((statusName): string => {
                    switch (statusName) {
                      case "approved":
                        return "#37b53c";
                      case "rejected":
                        return "#e1605e";
                      case "counter":
                        return "#ffb03a";
                      case "expired":
                        return "#e1605e";
                      default:
                        return "";
                    }
                  })(data?.status)}`,
                }}
              >
                {`${capitalize(data?.status ?? "")} ${
                  data?.status === "counter"
                    ? `${new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                      }).format(data.rate)}USD`
                    : " "
                } `}
              </div>
            ),
          },
          align: "center",
        },
      ];
    }

    return columns;
  }, [sortByValues, showStatus]);

  return { logsTableColumns, sortByValues };
};
